// ============================================================
// 阴盘奇门 · 交叉分析层（供 UI 判读与 AI 接地数据共用）
// 依据：阴盘奇门「以象意为先、以宫位为体」的读盘法，配合传统用神体系：
//   日干＝求测人（自身）、时干＝所测之事；再按问事类别增补用神。
//   宫与宫的五行生克、门宫关系、星宫旺衰、天地盘十干克应、四害（空亡/门迫/
//   击刑/入墓）、马星与值符值使，构成判断骨架；阴阳盘法度不同，本盘为阴盘。
// 说明：十干克应表取传统通行格名（天盘干加地盘干），表中未列者按五行生克与干象断。
// ============================================================

import { GONG_BY_NUM, MENS, MEN_BY_NAME, STARS, STAR_BY_NAME, WX } from './constants.js';
import { GAN_MEANINGS, MEN_MEANINGS, SHEN_MEANINGS, STAR_MEANINGS } from './meanings.js';

const SHENG = { 木: '火', 火: '土', 土: '金', 金: '水', 水: '木' };
const KE = { 木: '土', 土: '水', 水: '火', 火: '金', 金: '木' };
const ZHI_WX = {
  子: '水', 丑: '土', 寅: '木', 卯: '木', 辰: '土', 巳: '火',
  午: '火', 未: '土', 申: '金', 酉: '金', 戌: '土', 亥: '水'
};

/** 两五行之间的关系（a 为主体、b 为客体） */
export function wxRelation(a, b) {
  if (!a || !b) return { key: '—', kind: '—', actor: 'none', tone: '平', text: '五行不全，不能断生克' };
  if (a === b) return { key: '比和', kind: '比', actor: 'both', tone: '平', text: `同为${a}，比和，主同类相帮、势均力敌` };
  if (SHENG[a] === b) return { key: `${a}生${b}`, kind: '生', actor: 'a', tone: '吉', text: `${a}生${b}，主我付出、滋养对方` };
  if (SHENG[b] === a) return { key: `${b}生${a}`, kind: '生', actor: 'b', tone: '吉', text: `${b}生${a}，主外来生助、得利` };
  if (KE[a] === b) return { key: `${a}克${b}`, kind: '克', actor: 'a', tone: '凶', text: `${a}克${b}，主我能制事、但须费力` };
  return { key: `${b}克${a}`, kind: '克', actor: 'b', tone: '凶', text: `${b}克${a}，主外来压制、事多阻` };
}

/** 月令五行：由四柱月支取（阴盘断旺衰以月令为主） */
export function monthWx(chart) {
  const zhi = String(chart?.fourPillars?.month || '').slice(1, 2);
  return { zhi, wx: ZHI_WX[zhi] || null };
}

/** 天盘干 / 地盘干落宫（天盘优先，多宫并列）；甲不现盘面，以值符代之 */
export function locateGan(chart, gan) {
  if (gan === '甲') {
    const zf = (chart?.palaces || []).find((x) => x.isZhiFu);
    return zf ? [{ num: zf.num, via: '值符代甲' }] : [];
  }
  const hits = [];
  for (const p of chart?.palaces || []) {
    if (p.center) continue;
    const tp = Array.isArray(p.tianpan) ? p.tianpan : [];
    if (tp.includes(gan)) hits.push({ num: p.num, via: '天盘' });
    if (p.dipan === gan) hits.push({ num: p.num, via: '地盘' });
  }
  return hits;
}

/** 找某星 / 某门 / 某神所在之宫 */
export function locateSymbol(chart, name, type) {
  return (chart?.palaces || [])
    .filter((p) => !p.center)
    .filter((p) => {
      if (type === 'star') return (p.stars || []).includes(name);
      if (type === 'men') return p.men === name;
      if (type === 'shen') return p.shen === name;
      return false;
    })
    .map((p) => ({ num: p.num, via: type === 'star' ? '星' : type === 'men' ? '门' : '神' }));
}

/** 各类问事的用神取向（阴盘：以象意取用，日干／时干为纲） */
export const YONG_SHEN_RULES = {
  career: [
    { type: 'gan', name: 'DAY', role: '求测人', note: '日干为本人' },
    { type: 'men', name: '开门', role: '事业／工作', note: '开门主事业、开工、官方' },
    { type: 'shen', name: '值符', role: '领导／贵人', note: '值符为贵神、上司、权威' }
  ],
  wealth: [
    { type: 'gan', name: 'DAY', role: '求测人', note: '日干为本人' },
    { type: 'men', name: '生门', role: '财源生意', note: '生门主财利、生意' },
    { type: 'gan', name: '戊', role: '资本本钱', note: '戊为资本、本钱、资产' }
  ],
  love: [
    { type: 'gan', name: 'DAY', role: '己方', note: '日干为求测人' },
    { type: 'gan', name: 'HOUR', role: '对方／此事', note: '时干为所问之事（对方）' },
    { type: 'shen', name: '六合', role: '媒合婚姻', note: '六合主婚姻、和合、中介' },
    { type: 'men', name: '休门', role: '婚恋安逸', note: '休门利婚姻和合' }
  ],
  health: [
    { type: 'gan', name: 'DAY', role: '求测人', note: '日干为本人' },
    { type: 'star', name: '天芮', role: '病星', note: '天芮为病，看其落宫与克应' },
    { type: 'star', name: '天心', role: '医药', note: '天心为医、为药' },
    { type: 'shen', name: '白虎', role: '凶伤', note: '白虎主凶伤、急症' }
  ],
  study: [
    { type: 'gan', name: 'DAY', role: '求测人', note: '日干为本人' },
    { type: 'star', name: '天辅', role: '文昌', note: '天辅为文昌星，主学业考试' },
    { type: 'men', name: '景门', role: '文书', note: '景门主文书、文章、考试' }
  ],
  travel: [
    { type: 'gan', name: 'DAY', role: '求测人', note: '日干为本人' },
    { type: 'ma', name: 'MA', role: '驿马', note: '马星主出行、调动、变动' },
    { type: 'men', name: '开门', role: '出行门户', note: '开门主出行、门户' }
  ],
  legal: [
    { type: 'gan', name: 'DAY', role: '求测人', note: '日干为本人' },
    { type: 'men', name: '惊门', role: '官非口舌', note: '惊门主惊恐、官司、口舌' },
    { type: 'shen', name: '白虎', role: '对头刑伤', note: '白虎主争斗、刑伤' }
  ],
  lost: [
    { type: 'gan', name: 'DAY', role: '失主', note: '日干为失主本人' },
    { type: 'shen', name: '玄武', role: '盗失', note: '玄武主盗窃、暗失' },
    { type: 'gan', name: '戊', role: '财物', note: '戊为财物、贵重之物' }
  ],
  cooperation: [
    { type: 'gan', name: 'DAY', role: '己方', note: '日干为求测人' },
    { type: 'gan', name: 'HOUR', role: '对方／合作事', note: '时干为合作之事' },
    { type: 'shen', name: '六合', role: '合作中介', note: '六合主合作、中介、和合' }
  ],
  general: [
    { type: 'gan', name: 'DAY', role: '求测人', note: '日干为本人' },
    { type: 'gan', name: 'HOUR', role: '所测之事', note: '时干为所问之事' }
  ]
};

export const CATEGORY_LABEL = {
  career: '事业／工作', wealth: '财运／求财', love: '感情／婚姻', health: '健康',
  study: '学业／考试', travel: '出行／搬迁', legal: '官司／竞争', lost: '寻物／寻人',
  cooperation: '合作／谈判', general: '综合趋势'
};

/** 定用神：日干／时干为纲，按类别增补；返回落宫与象意 */
export function locateYongShen(chart, category = 'general') {
  const rules = YONG_SHEN_RULES[category] || YONG_SHEN_RULES.general;
  const dayGan = String(chart?.fourPillars?.day || '').slice(0, 1);
  const hourGan = String(chart?.fourPillars?.hour || '').slice(0, 1);
  const items = [];

  const push = (o) => { if (o && o.palaces && o.palaces.length) items.push(o); };

  for (const r of rules) {
    if (r.type === 'gan') {
      const gan = r.name === 'DAY' ? dayGan : r.name === 'HOUR' ? hourGan : r.name;
      if (!gan) {
        items.push({ role: r.role, kind: '干', symbol: '—', note: r.note, palaces: [], plain: '四柱不全' });
        continue;
      }
      const hits = locateGan(chart, gan);
      items.push({
        role: r.role, kind: '干', symbol: gan, note: r.note,
        palaces: hits.map((h) => h.num), vias: hits.map((h) => h.via),
        plain: gan === '甲' ? '甲不现盘面，遁于六仪之下，以值符代之' : (GAN_MEANINGS[gan]?.text || '')
      });
    } else if (r.type === 'ma') {
      const gong = chart?.maXing?.gong;
      items.push({
        role: r.role, kind: '马', symbol: chart?.maXing?.zhi || '—', note: r.note,
        palaces: gong ? [gong] : [], vias: ['时支马'],
        plain: '马星主驿动、出行、调动、变数'
      });
    } else {
      const hits = locateSymbol(chart, r.name, r.type);
      const plain = r.type === 'men' ? MEN_MEANINGS[r.name]?.text
        : r.type === 'star' ? STAR_MEANINGS[r.name]?.text
          : SHEN_MEANINGS[r.name]?.text;
      items.push({
        role: r.role, kind: r.type === 'men' ? '门' : r.type === 'star' ? '星' : '神',
        symbol: r.name, note: r.note,
        palaces: hits.map((h) => h.num), vias: hits.map((h) => h.via),
        plain: plain || ''
      });
    }
  }
  return items;
}

/** 门与宫的关系：门迫（门克宫）／门生宫／宫生门／宫克门／比和 */
export function menPalaceRelation(menName, palaceWx) {
  const m = MEN_BY_NAME[menName];
  if (!m || !palaceWx) return null;
  const rel = wxRelation(m.wx, palaceWx);
  if (rel.kind === '比') return { key: '门宫比和', tone: '平', detail: `${menName}(${m.wx})与宫同气` };
  if (rel.kind === '克' && rel.actor === 'a') return { key: '门迫', tone: '凶', detail: `${menName}(${m.wx})克宫(${palaceWx})，门迫之象，吉门减吉、凶门增凶` };
  if (rel.kind === '克' && rel.actor === 'b') return { key: '宫克门', tone: '凶', detail: `宫(${palaceWx})克${menName}(${m.wx})，门受制，事难施展` };
  if (rel.kind === '生' && rel.actor === 'b') return { key: '宫生门', tone: '吉', detail: `宫(${palaceWx})生${menName}(${m.wx})，宫门相得，得地助力` };
  return { key: '门生宫', tone: '吉', detail: `${menName}(${m.wx})生宫(${palaceWx})，门来生宫，外出顺利` };
}

/** 星与宫的关系 + 旺衰（以月令） */
export function starPalaceRelation(starName, palaceWx, mWx) {
  const s = STAR_BY_NAME[starName];
  if (!s) return null;
  const rel = wxRelation(s.wx, palaceWx);
  let wangShuai = '休囚';
  if (mWx) {
    if (s.wx === mWx) wangShuai = '得令（旺）';
    else if (SHENG[mWx] === s.wx) wangShuai = '得生（相）';
    else if (SHENG[s.wx] === mWx) wangShuai = '泄气（休）';
    else if (KE[mWx] === s.wx) wangShuai = '受克（囚）';
    else if (KE[s.wx] === mWx) wangShuai = '克令（死）';
  }
  return { rel, wangShuai, luck: s.luck };
}

/** 十干克应（天盘干加地盘干）。表中为传统通行格名；未列者按五行取象。 */
const KEYING = {
  '戊乙': ['青龙合会', '合格，事必牵连他人他事；得吉门则吉，凶门则凶', '平'],
  '戊丙': ['青龙返首', '谋事大利，遇反吟则成中有败', '吉'],
  '戊丁': ['青龙耀明', '谒贵求名可成，宜见贵人', '吉'],
  '戊戊': ['伏吟', '天地盘同干，事多阻滞，宜守不宜动', '凶'],
  '戊己': ['贵人入狱', '公私皆不利，贵人受困', '凶'],
  '戊庚': ['值符飞宫', '主换地方、须变动；吉事转凶', '凶'],
  '戊辛': ['青龙折足', '半道折损，最忌投资，防钱上犯错', '凶'],
  '戊壬': ['青龙入天牢', '权位受困、运气低潮，忌投资', '凶'],
  '戊癸': ['青龙华盖', '合格，必牵连他人；吉门吉格则福', '平'],
  '乙戊': ['阴害', '利阴不利阳，防暗中破财', '凶'],
  '乙乙': ['日奇伏吟', '谒贵求名难成，宜守旧', '凶'],
  '乙丙': ['奇仪顺遂', '谋事顺遂，婚恋多吉', '吉'],
  '乙丁': ['奇仪相佐', '文书信息得利，宜谋划', '吉'],
  '乙己': ['日奇入墓', '暗昧拖沓，事不明朗', '凶'],
  '乙庚': ['日奇被刑', '争讼、婚不和，防口舌', '凶'],
  '乙辛': ['青龙逃走', '人走财散，防拐骗、合作生变', '凶'],
  '乙壬': ['日奇入地', '暗昧牵连，多涉妇人', '平'],
  '乙癸': ['华盖逢星', '宜隐遁守静，不宜张扬', '平'],
  '丙戊': ['飞鸟跌穴', '大吉，事速成、谋为皆利', '吉'],
  '丙乙': ['日月并行', '公私皆吉，事有双美', '吉'],
  '丙丙': ['月奇悖师', '文书破耗，急躁致乱', '凶'],
  '丙丁': ['星奇朱雀', '文书信息通达，吉', '吉'],
  '丙己': ['火悖入刑', '文书不利，防刑责口舌', '凶'],
  '丙庚': ['荧入太白', '敌退我进之象，宜退不宜进', '平'],
  '丙辛': ['日月相会', '谋事成就，婚姻相合', '吉'],
  '丙壬': ['火入天罗', '为客不利，是非丛生', '凶'],
  '丙癸': ['华盖悖师', '阴人害事，灾祸频生', '凶'],
  '丁戊': ['青龙转光', '贵人升迁，事有转机', '吉'],
  '丁乙': ['人遁吉格', '贵人加官进爵，婚恋吉', '吉'],
  '丁丙': ['星随月转', '贵人越级；常人乐中生悲', '平'],
  '丁丁': ['星奇伏吟', '文书阻隔，宜静候', '凶'],
  '丁己': ['火入勾陈', '奸私仇怨，事多出女人', '凶'],
  '丁庚': ['文书阻隔', '行人必归，消息迟滞', '平'],
  '丁辛': ['朱雀入狱', '罪人得释、官人失位，是非翻转', '平'],
  '丁壬': ['五神互合', '贵人恩诏，讼狱得平', '吉'],
  '丁癸': ['朱雀投江', '音信沉溺，消息不通', '凶'],
  '庚戊': ['太白天乙伏宫', '主变动、换地方，不变不行', '平'],
  '庚乙': ['太白逢星', '退吉进凶，宜守', '平'],
  '庚丙': ['太白入荧', '贼必来，为客进利、防侵夺', '凶'],
  '庚丁': ['亭亭之格', '因私匿起官司是非，家宅口舌', '凶'],
  '庚己': ['官符刑格', '官司口舌、刑伤难受', '凶'],
  '庚庚': ['太白同宫（战格）', '冲格，最不利合作，争斗内乱', '凶'],
  '庚辛': ['白虎干格', '防打架受伤、车祸之灾', '凶'],
  '庚壬': ['上格（小格）', '动荡奔波，频繁跑动', '平'],
  '庚癸': ['大格', '五湖四海奔走，主大变动、谋事难成', '凶'],
  '辛戊': ['困龙被伤', '必主破财，运势困顿', '凶'],
  '辛乙': ['白虎猖狂', '人亡家败，远行多殃，利客不利主', '凶'],
  '辛丙': ['干合悖师', '因财致讼，合作出口舌', '凶'],
  '辛丁': ['狱神得奇', '囚人得释，商人获利', '吉'],
  '辛己': ['入狱自刑', '奴仆背主，有苦难申，忌合作', '凶'],
  '辛庚': ['白虎出力', '刀刃相交，主客两伤', '凶'],
  '辛辛': ['伏吟天庭', '公废私就，讼狱自罹', '凶'],
  '辛壬': ['凶蛇入狱', '两男争女，讼狱不息', '凶'],
  '辛癸': ['天牢华盖', '日月失明，误入天网，宜静守', '凶'],
  '壬戊': ['小蛇化龙', '企业发展、事业转机，利投资；测病则病进', '吉'],
  '壬己': ['反吟蛇刑', '官司败诉，顺守可吉', '凶'],
  '壬庚': ['太白擒蛇', '刑狱公平，是非立剖', '吉'],
  '壬丙': ['水蛇入火', '官灾刑禁，是非络绎', '凶'],
  '壬丁': ['干合蛇刑', '文书牵连，贵人匆匆', '平'],
  '壬壬': ['蛇入地罗', '外人缠绕，内事琐碎', '凶'],
  '壬辛': ['腾蛇相缠', '纵有吉门亦不能安', '凶'],
  '壬癸': ['幼女奸淫', '家丑外露，防暗昧之事', '凶'],
  '癸戊': ['天乙会合', '吉门宜求财，婚姻多吉', '吉'],
  '癸乙': ['华盖逢星', '贵人得禄，常人平安', '吉'],
  '癸丙': ['华盖悖师', '贵贱同视，上人见喜', '平'],
  '癸丁': ['螣蛇夭矫', '文书官司，防火灾口舌', '凶'],
  '癸己': ['华盖地户', '音信阻隔，宜静守待时', '凶'],
  '癸庚': ['太白入网', '暴力争讼，自罹罪责', '凶'],
  '癸辛': ['网盖天牢', '官司败诉，事难脱身', '凶'],
  '癸壬': ['复见螣蛇', '嫁娶重婚之象，事宜慎', '凶'],
  '癸癸': ['天网四张', '行人失伴，病讼皆伤，宜静', '凶']
};

/** 天地盘十干克应：逐天盘干与地盘干组合 */
export function ganKeying(tianpanList, diGan) {
  const out = [];
  const tp = Array.isArray(tianpanList) ? tianpanList : [];
  for (const t of tp) {
    if (!t || !diGan) continue;
    const key = t + diGan;
    const hit = KEYING[key];
    const rel = wxRelation(WX[t], WX[diGan]);
    const name = hit ? hit[0] : (t === diGan ? '伏吟' : rel.kind === '克' ? '冲格' : rel.kind === '生' ? '合格' : '比和格');
    const text = hit ? hit[1] : `天盘${t}加地盘${diGan}：${rel.text}`;
    const tone = hit ? hit[2] : rel.tone;
    out.push({ pair: `${t}加${diGan}`, name, text, tone, tian: t, di: diGan });
  }
  return out;
}

/** 单宫读象（阴盘以象意为先）：神＋门＋星＋干 的组合意象 */
export function palaceReading(chart, num) {
  const p = (chart?.palaces || []).find((x) => x.num === Number(num));
  if (!p) return null;
  if (p.center) {
    return { num: p.num, gua: '中', wx: '土', center: true, text: p.note || '中五宫无星门', items: [] };
  }
  const g = GONG_BY_NUM[p.num];
  const mWx = monthWx(chart).wx;
  const menRel = p.men ? menPalaceRelation(p.men, g.wx) : null;
  const starRels = (p.stars || []).map((sn) => ({ star: sn, ...(starPalaceRelation(sn, g.wx, mWx) || {}) }));
  const keying = ganKeying(p.tianpan, p.dipan);
  const gans = (p.tianpan || []).map((t) => ({ gan: t, ...(GAN_MEANINGS[t] || {}) }));

  const items = [];
  if (p.shen) items.push({ kind: '神', name: p.shen, text: SHEN_MEANINGS[p.shen]?.text || '', luck: SHEN_MEANINGS[p.shen]?.luck });
  if (p.men) items.push({ kind: '门', name: p.men, text: MEN_MEANINGS[p.men]?.text || '', luck: MEN_MEANINGS[p.men]?.luck, rel: menRel });
  for (const s of starRels) items.push({ kind: '星', name: s.star, text: STAR_MEANINGS[s.star]?.text || '', luck: s.luck, rel: s.rel, wangShuai: s.wangShuai });
  for (const gn of gans) items.push({ kind: '干', name: gn.gan, text: gn.text || '', tag: gn.tag });

  return {
    num: p.num, gua: g.gua, dir: g.dir, wx: g.wx, center: false,
    dipan: p.dipan, jigongDiGan: p.jigongDiGan || null,
    tianpan: p.tianpan || [], stars: p.stars || [], men: p.men, shen: p.shen,
    harms: p.harms || [], isMa: !!p.isMa, isZhiFu: !!p.isZhiFu, isZhiShi: !!p.isZhiShi,
    menRel, starRels, keying, items,
    text: [p.shen, p.men, (p.stars || []).join(''), (p.tianpan || []).join('') + '加' + (p.dipan || '')]
      .filter(Boolean).join(' · ')
  };
}

/** 全盘交叉判读：用神主轴 + 关键宫 + 四害 + 应期线索 + 倾向 */
export function crossReading(chart, category = 'general') {
  if (!chart || !Array.isArray(chart.palaces)) return null;
  const mWx = monthWx(chart);
  const yong = locateYongShen(chart, category);

  const dayGan = String(chart.fourPillars?.day || '').slice(0, 1);
  const hourGan = String(chart.fourPillars?.hour || '').slice(0, 1);
  const selfPalaces = locateGan(chart, dayGan).map((h) => h.num);
  const matterPalaces = locateGan(chart, hourGan).map((h) => h.num);
  const selfNum = selfPalaces[0] ?? null;
  const matterNum = matterPalaces[0] ?? null;

  const selfP = selfNum ? palaceReading(chart, selfNum) : null;
  const matterP = matterNum ? palaceReading(chart, matterNum) : null;

  // 主轴：求测人宫 vs 事体宫
  let axis = null;
  if (selfP && matterP && selfNum !== matterNum) {
    const rel = wxRelation(selfP.wx, matterP.wx);
    const palaceTone = rel.kind === '比' ? '平' : (rel.actor === 'b' && rel.kind === '生') || (rel.actor === 'a' && rel.kind === '克') ? '吉' : '凶';
    axis = {
      self: { num: selfP.num, gua: selfP.gua, wx: selfP.wx },
      matter: { num: matterP.num, gua: matterP.gua, wx: matterP.wx },
      rel: rel.key, kind: rel.kind, actor: rel.actor, tone: palaceTone, text: rel.text,
      same: false
    };
  } else if (selfP && matterP) {
    axis = {
      self: { num: selfP.num, gua: selfP.gua, wx: selfP.wx },
      matter: { num: matterP.num, gua: matterP.gua, wx: matterP.wx },
      rel: '同宫', kind: '同宫', actor: 'both', tone: '平',
      text: '求测人与所测之事同落一宫，事与身一体，看此宫组合定吉凶'
    };
  }

  // 关键宫：值符宫、值使宫、马星宫、以及用神落宫
  const keyPalaces = [];
  const seen = new Set();
  const addKey = (num, reason) => {
    if (!num || num === 5 || seen.has(num + reason)) return;
    seen.add(num + reason);
    keyPalaces.push({ num, reason });
  };
  (chart.palaces || []).forEach((p) => {
    if (p.isZhiFu) addKey(p.num, '值符所临');
    if (p.isZhiShi) addKey(p.num, '值使所临');
    if (p.isMa) addKey(p.num, '驿马所临');
  });
  yong.forEach((y) => y.palaces.forEach((n) => addKey(n, `${y.role}（${y.symbol}）`)));

  // 四害汇总
  const harms = [];
  (chart.palaces || []).forEach((p) => {
    (p.harms || []).forEach((h) => harms.push({ num: p.num, gua: GONG_BY_NUM[p.num]?.gua, type: h.type, label: h.label, detail: h.detail }));
  });

  // 应期线索（阴盘/传统共用）：空亡填实、马星、墓库冲开、值使门
  const timing = [];
  harms.filter((h) => h.type === '空亡').forEach((h) => timing.push(`宫${h.num}（${h.label}）逢空：待冲实或填实之期方见分晓（${h.detail}）`));
  if (chart.maXing?.gong) timing.push(`马星在${chart.maXing.zhi}，落宫${chart.maXing.gong}：马动之月日应事，主动、主出行调动`);
  harms.filter((h) => h.type === '入墓').forEach((h) => timing.push(`宫${h.num} 入墓：待冲开墓库之期（${h.detail}）`));
  if (chart.zhiShi) {
    const zs = (chart.palaces || []).find((p) => p.men === chart.zhiShi);
    if (zs) timing.push(`值使${chart.zhiShi}落宫${zs.num}：以值使门当值为应期参考`);
  }

  // 倾向：宫位关系 + 两宫吉凶象 + 四害数
  let score = 0;
  if (axis) score += axis.tone === '吉' ? 2 : axis.tone === '凶' ? -2 : 0;
  const luckScore = (p) => {
    if (!p) return 0;
    let s = 0;
    const menLuck = MEN_BY_NAME[p.men]?.luck;
    if (menLuck === '吉') s += 1; else if (menLuck === '凶') s -= 1;
    if (p.menRel?.key === '门迫') s -= 1;
    if (p.starRels?.some((x) => x.luck === '吉')) s += 0.5;
    if (p.starRels?.some((x) => x.luck === '凶')) s -= 0.5;
    if (p.shen && ['值符', '太阴', '六合', '九天', '九地'].includes(p.shen)) s += 0.5;
    if (p.shen && ['螣蛇', '白虎', '玄武'].includes(p.shen)) s -= 0.5;
    s -= (p.harms || []).length * 0.5;
    return s;
  };
  score += (luckScore(selfP) + luckScore(matterP)) / 2;

  const tone = score >= 2 ? '偏吉 · 可进' : score > 0 ? '略吉 · 宜主动' : score >= -1.5 ? '中平 · 量力而行' : score >= -3 ? '偏阻 · 宜守' : '受阻 · 先解压力';
  const toneColor = score >= 2 ? '吉' : score > 0 ? '吉' : score >= -1.5 ? '平' : '凶';

  // 逐宫的十干克应与门宫关系（供 AI 接地：只可引用盘面实有的组合）
  const palaces = (chart.palaces || [])
    .filter((p) => !p.center)
    .map((p) => {
      const pr = palaceReading(chart, p.num);
      return {
        num: p.num,
        gua: pr.gua,
        wx: pr.wx,
        menRel: pr.menRel ? { key: pr.menRel.key, tone: pr.menRel.tone } : null,
        keying: pr.keying.map((k) => ({ pair: k.pair, name: k.name, tone: k.tone }))
      };
    });

  return {
    category, categoryLabel: CATEGORY_LABEL[category] || '综合趋势',
    monthWx: mWx,
    yong,
    axis,
    palaces,
    selfPalace: selfP,
    matterPalace: matterP,
    keyPalaces,
    harms,
    timing,
    score: Math.round(score * 10) / 10,
    tone,
    toneColor
  };
}
/** 每宫的「与所问之事相关」角色标签（用于九宫盘面标注） */
export function palaceRoles(chart, category = 'general') {
  const map = {};
  const put = (num, label, tone) => {
    if (!num) return;
    map[num] = map[num] || [];
    if (!map[num].some((x) => x.label === label)) map[num].push({ label, tone: tone || 'key' });
  };
  const yong = locateYongShen(chart, category);
  yong.forEach((y) => y.palaces.forEach((n) => put(n, y.symbol === 'MA' ? '马星' : `${y.role}`, 'yong')));
  (chart?.palaces || []).forEach((p2) => {
    if (p2.isZhiFu) put(p2.num, '值符', 'fu');
    if (p2.isZhiShi) put(p2.num, '值使', 'shi');
    if (p2.isMa) put(p2.num, '马', 'ma');
  });
  return map;
}

/** 宫位之间的连线关系（供 SVG 叠层画生克箭头） */
export function crossLinks(chart, category = 'general') {
  const rd = crossReading(chart, category);
  if (!rd) return [];
  const links = [];
  if (rd.axis && rd.self && rd.matter && rd.self.num !== rd.matter.num) {
    links.push({
      from: rd.self.num, to: rd.matter.num,
      kind: rd.axis.kind, tone: rd.axis.tone, label: rd.axis.rel,
      title: `求测人宫 → 事体宫：${rd.axis.text}`
    });
  }
  // 用神（门/星/神/干）落宫 → 求测人宫 的关系
  const selfNum = rd.selfPalace?.num;
  rd.yong.forEach((y) => {
    if (!selfNum) return;
    y.palaces.forEach((n) => {
      if (n === selfNum) return;
      const a = palaceReading(chart, selfNum);
      const b = palaceReading(chart, n);
      if (!a || !b) return;
      const rel = wxRelation(a.wx, b.wx);
      links.push({
        from: n, to: selfNum, kind: rel.kind, tone: rel.kind === '克' ? '凶' : rel.kind === '比' ? '平' : '吉',
        label: rel.key, title: `${y.role}（${y.symbol}）所在宫 → 求测人宫：${rel.text}`
      });
    });
  });
  // 去重（同起止同标签）
  const seen = new Set();
  return links.filter((l) => {
    const k = `${l.from}-${l.to}-${l.label}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
